import { clearCacheItem } from '@propertypal/shared/src/hooks/useApi';
import { deleteNote, saveNote } from '@propertypal/shared/src/reducers/notes';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import { parseApiDate } from '@propertypal/shared/src/services/dayjs';
import { Note } from '@propertypal/shared/src/types/notes';
import Loader from '@propertypal/web-ui/src/animation/Loader.style';
import LoadingText from '@propertypal/web-ui/src/animation/LoadingText';
import PropertyPalAutoSave from '@propertypal/web-ui/src/forms-deprecated/auto-save/PropertyPalAutoSave';
import PropertyPalForm from '@propertypal/web-ui/src/forms-deprecated/form/PropertyPalForm';
import PropertyPalTextArea from '@propertypal/web-ui/src/forms-deprecated/textarea/PropertyPalTextArea';
import MiniIconNotification from '@propertypal/web-ui/src/notifications/MiniIconNotification';
import ProfileAvatar from '@propertypal/web-ui/src/profile/ProfileAvatar';
import { FormikProps, FormikValues } from 'formik';
import React, { RefObject, useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { format } from 'timeago.js';
import {
  NoteItemContainer,
  NoteItemContent,
  NoteItemDate,
  NoteItemTime,
  NoteItemFormWrapper,
  NoteItemDetail,
  NoteItemFormNotification,
} from './Notes.style';

interface NoteItemProps {
  note?: Note;
  hardDelete?: boolean;
  textAreaBg?: string;
  isLoading?: boolean;
  notificationText?: string;
  setNotificationText?: React.Dispatch<React.SetStateAction<string | undefined>>;
  updating?: boolean;
  setUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  testID?: string;
  onNoteUpdate?: () => void;
}

// 4:3 Ratio, img will be no larger than 340px
const IMAGE_LOCATOR = '340x255:FILL_CROP';
const AUTO_SAVE_DELAY = process?.env?.JEST_WORKER_ID ? 0 : 2000;

const NoteItem: React.FC<NoteItemProps> = ({
  note,
  hardDelete = false,
  textAreaBg,
  isLoading,
  notificationText,
  setNotificationText,
  updating,
  setUpdating,
  testID,
  onNoteUpdate,
}) => {
  const dispatch = useAppDispatch();
  const mounted = useRef<boolean>(false);
  const formRef = useRef(null) as RefObject<FormikProps<FormikValues>>;
  const theme = useTheme();
  const name = `${note?.firstName || ''} ${note?.surname || ''}`;
  const initialValues = {
    note: note?.content || '',
  };

  const getDate = () => {
    if (isLoading) return <LoadingText data-testid="note-date-loading" textLength={5} display="inline-flex" />;
    if (note?.timeModified) return parseApiDate(note?.timeModified).format('DD MMM YYYY');
    return null;
  };

  const getTime = () => {
    if (isLoading) return <LoadingText data-testid="note-time-loading" textLength={9} display="inline-flex" />;
    if (note?.timeModified)
      return (
        <>
          {format(note?.timeModified, 'en_GB')} <strong>{note?.selfAuthored ? 'you' : name}</strong> wrote:
        </>
      );
    return null;
  };

  const onFormSubmit = async (values: any) => {
    if (setUpdating) setUpdating(true);
    const { note: value } = values;

    if (note?.id) {
      if (!value || value?.length === 0) {
        const del = await dispatch(deleteNote(note?.id, !!hardDelete, IMAGE_LOCATOR));
        // hardDelete will unmount component, so we only update state on a mounted component
        if (mounted.current) {
          if (setUpdating) setUpdating(false);
          if (del && setNotificationText) setNotificationText('Removed!');
          if (onNoteUpdate) onNoteUpdate();
        }
      } else {
        const save = await dispatch(saveNote(note?.id, value, IMAGE_LOCATOR));
        if (setUpdating) setUpdating(false);
        if (setNotificationText && save)
          setNotificationText(!note?.content || note?.content.length === 0 ? 'Saved!' : 'Updated!');
        if (onNoteUpdate) onNoteUpdate();
      }
      // invalidate property cache with a regex match.
      clearCacheItem(new RegExp(`/property/${note.id}?currency=`));
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <NoteItemContainer data-testid={testID || 'note-item'}>
      <ProfileAvatar
        testID={`note-avatar${isLoading ? '-loading' : ''}`}
        alt={`${name} Avatar`}
        src={note?.authorAvatar}
        isLoading={isLoading}
        mr={15}
        mt={23}
        boxShadow
      />
      <NoteItemContent>
        <NoteItemDate data-testid="note-date">{getDate()}</NoteItemDate>
        <NoteItemTime data-testid="note-time">{getTime()}</NoteItemTime>

        {isLoading && (
          <NoteItemFormWrapper data-testid="note-item-loading">
            <Loader w="100%" h="160px" display="block" />
          </NoteItemFormWrapper>
        )}

        {!isLoading && note?.selfAuthored && (
          <PropertyPalForm enableReinitialize initialValues={initialValues} onSubmit={onFormSubmit} formRef={formRef}>
            <>
              <NoteItemFormWrapper>
                <PropertyPalTextArea
                  name="note"
                  rows={6}
                  bg={textAreaBg || theme.white}
                  placeholder="Make a note about this property"
                />
                {setNotificationText && (
                  <NoteItemFormNotification data-testid="note-form-notfication">
                    <MiniIconNotification
                      text={notificationText}
                      setNotificationText={setNotificationText}
                      loading={!!updating}
                    />
                  </NoteItemFormNotification>
                )}
              </NoteItemFormWrapper>
              <PropertyPalAutoSave onSubmit={onFormSubmit} delay={AUTO_SAVE_DELAY} />
            </>
          </PropertyPalForm>
        )}

        {!isLoading && !note?.selfAuthored && (
          <NoteItemDetail data-testid="note-detail">{note?.content}</NoteItemDetail>
        )}
      </NoteItemContent>
    </NoteItemContainer>
  );
};

export default NoteItem;
